import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { formatMoneyCents } from '../../../theme/chart';
import { tokens } from '../../../theme/tokens';
import { parseLocalDate } from '../../../utils/bills';
import type { PendingBill } from './dashboard.types';
import { Panel, PanelHeader, Quiet } from './ui';

const DAY = 24 * 60 * 60 * 1000;

/** Days from today to a plain calendar date; negative once it is past. */
const daysUntil = (iso: string, now: Date) => {
  const due = parseLocalDate(iso);
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((due.getTime() - today.getTime()) / DAY);
};

/** "Contas a vencer": the next open bills, the urgent ones said in words. */
export function PendingBillsCard({ bills, onSeeAll, now = new Date() }: { bills: PendingBill[]; onSeeAll?: () => void; now?: Date }) {
  const { t, i18n } = useTranslation('dashboard');
  const locale = i18n.language;

  const when = (iso: string): { text: string; urgent: boolean } => {
    const days = daysUntil(iso, now);
    if (days < 0) return { text: t('home.bills.overdue'), urgent: true };
    if (days === 0) return { text: t('home.bills.today'), urgent: true };
    if (days === 1) return { text: t('home.bills.tomorrow'), urgent: true };
    return { text: parseLocalDate(iso).toLocaleDateString(locale, { day: 'numeric', month: 'short' }), urgent: false };
  };

  return (
    <Panel label={t('home.bills.title')}>
      <PanelHeader title={t('home.bills.title')} action={onSeeAll ? { label: t('home.seeAll'), onClick: onSeeAll } : undefined} />
      {bills.length === 0 ? (
        <Quiet>{t('home.noPendingBills')}</Quiet>
      ) : (
        <Box component='ul' sx={{ m: 0, p: 0, listStyle: 'none' }}>
          {bills.map((bill, index) => {
            const due = when(bill.dueDate);
            return (
              <Box
                component='li'
                key={bill.id}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 1.25,
                  py: 1.25,
                  borderTop: index ? `1px solid ${tokens.line}` : 'none',
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                  <Box component='span' sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{bill.name}</Box>
                  <Box component='span' sx={{ fontSize: 12.5, fontWeight: due.urgent ? 700 : 400, color: due.urgent ? tokens.warn : tokens.ink3 }}>
                    {due.text}
                  </Box>
                </Box>
                <Box component='strong' sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                  {formatMoneyCents(bill.amount, bill.currency, locale)}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Panel>
  );
}
